const InvalidSubmitFocusUtils = require('hooks/useFormPrimitives/invalidSubmitFocusUtils');

const FormSubmitUtils = module.exports = {};

FormSubmitUtils.ORIGINAL_HANDLE_SUBMIT_KEY = '__nearpeerOriginalHandleSubmit__';
FormSubmitUtils.WRAPPED_HANDLE_SUBMIT_KEY = '__nearpeerWrappedHandleSubmit__';

FormSubmitUtils.noop = () => undefined;

FormSubmitUtils.flattenFields = (fields = []) => fields.flatMap((field) => {

    if (!field) {
        return [];
    }

    if (Array.isArray(field)) {
        return FormSubmitUtils.flattenFields(field);
    }

    if (Array.isArray(field.fields)) {
        return FormSubmitUtils.flattenFields(field.fields);
    }

    return [field];
});

FormSubmitUtils.getInvalidSubmitFieldNames = (fields = []) => FormSubmitUtils.flattenFields(fields)
    .filter((field) => field?.name && field.render !== false && field.type !== 'hidden')
    .map((field) => field.name);

FormSubmitUtils.normalizePath = (path = '') => String(path)
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);

FormSubmitUtils.getPathValue = (source, path) => FormSubmitUtils.normalizePath(path)
    .reduce((value, key) => value?.[key], source);

FormSubmitUtils.hasErrorForField = ({
    errors = {},
    fieldName
}) => {

    if (!fieldName) {
        return false;
    }

    return !!FormSubmitUtils.getPathValue(errors, fieldName);
};

FormSubmitUtils.collectErrorNames = (errors = {}, prefix = '') => {

    if (!errors || typeof errors !== 'object') {
        return [];
    }

    return Object.entries(errors).flatMap(([key, value]) => {

        const fieldName = prefix ? `${prefix}.${key}` : key;

        if (!value || typeof value !== 'object') {
            return [fieldName];
        }

        if (value.message || value.type) {
            return [fieldName];
        }

        const childNames = FormSubmitUtils.collectErrorNames(value, fieldName);
        return childNames.length ? childNames : [fieldName];
    });
};

FormSubmitUtils.focusFirstInvalidField = ({
    errors = {},
    fields = [],
    formId,
    invalidFieldNames = []
}) => {

    const errorNames = FormSubmitUtils.collectErrorNames(errors);
    const fieldNames = invalidFieldNames.length
        ? invalidFieldNames
        : FormSubmitUtils.getInvalidSubmitFieldNames(fields);
    const candidateFieldNames = fieldNames.length
        ? fieldNames
        : errorNames;
    const formEl = formId && typeof document !== 'undefined'
        ? document.getElementById(formId)
        : null;

    InvalidSubmitFocusUtils.scheduleFocusFirstInvalidField({
        fields: candidateFieldNames,
        formEl,
        validate: (fieldName) => FormSubmitUtils.hasErrorForField({
            errors,
            fieldName
        })
    });
};

FormSubmitUtils.getLatestSubmitValues = ({
    methods,
    values
}) => {

    const latestValues = methods?.getValues?.();

    if (!latestValues || typeof latestValues !== 'object') {
        return values;
    }

    if (!values || typeof values !== 'object') {
        return latestValues;
    }

    return {
        ...values,
        ...latestValues
    };
};

FormSubmitUtils.wrapHandleSubmit = (methods = {}) => {

    if (typeof methods.handleSubmit !== 'function') {
        return methods;
    }

    const originalHandleSubmit = methods[FormSubmitUtils.ORIGINAL_HANDLE_SUBMIT_KEY] || methods.handleSubmit;

    if (methods[FormSubmitUtils.WRAPPED_HANDLE_SUBMIT_KEY]) {
        methods.handleSubmit = methods[FormSubmitUtils.WRAPPED_HANDLE_SUBMIT_KEY];
        return methods;
    }

    const wrappedHandleSubmit = (onValid, onInvalid) => originalHandleSubmit(
        typeof onValid === 'function'
            ? (values, event) => onValid(FormSubmitUtils.getLatestSubmitValues({
                methods,
                values
            }), event)
            : onValid,
        onInvalid
    );

    methods[FormSubmitUtils.ORIGINAL_HANDLE_SUBMIT_KEY] = originalHandleSubmit;
    methods[FormSubmitUtils.WRAPPED_HANDLE_SUBMIT_KEY] = wrappedHandleSubmit;
    methods.handleSubmit = wrappedHandleSubmit;

    return methods;
};

FormSubmitUtils.buildSubmitHandler = ({
    fields,
    formId,
    invalidFieldNames,
    methods,
    onInvalidSubmit,
    onSubmit = FormSubmitUtils.noop,
    onSubmitError
}) => {

    const handleSubmitError = (err, details, event) => {

        if (typeof onSubmitError === 'function') {
            return onSubmitError(err, details, event);
        }

        console.error(err);
        return undefined;
    };

    const handleValidSubmit = async (data, event) => {

        try {
            return await onSubmit(data, event);
        }
        catch (err) {
            return handleSubmitError(err, { data }, event);
        }
    };

    const handleInvalidSubmit = async (errors, event) => {

        FormSubmitUtils.focusFirstInvalidField({
            errors,
            fields,
            formId,
            invalidFieldNames
        });

        if (typeof onInvalidSubmit !== 'function') {
            return undefined;
        }

        try {
            return await onInvalidSubmit(errors, event);
        }
        catch (err) {
            return handleSubmitError(err, { errors }, event);
        }
    };

    return methods.handleSubmit(handleValidSubmit, handleInvalidSubmit);
};
